import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import Button from '@/components/Button';
import {
	CfCaptchaWidget,
	useCfTurnstileChallenge,
	useLoadCfTurnstileScript
} from '@/components/CfCaptchaWidget/index.js';
import ErrorMessage from '@/components/ErrorMessage';
import Input from '@/components/form/Input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/form/Select';
import FormTextArea from '@/components/form/TextArea';
import contactModel from '@/models/contact.js';
import routes from '@/routes';
import { localizedRoute } from '@/utils/locale';

import ImageDropdown from './ImageDropdown';
import { useReasons } from './hooks';

export const CONSTANTS = {
	ACCEPTED_TYPES: ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'],
	MAX_FILE_SIZE_MB: 2
};

function Form({ onSuccess }) {
	const [errorMessage, setErrorMessage] = useState('');
	const { t } = useTranslation();
	const reasonOptions = useReasons();
	const [loading, setLoading] = useState(false);

	useLoadCfTurnstileScript();

	const validationSchema = Yup.object({
		email: Yup.string().email().required(),
		reason: Yup.string().required(),
		subject: Yup.string().max(100).required(),
		message: Yup.string().max(500).required(),
		file: Yup.mixed()
			.optional()
			.nullable()
			.test({
				name: 'fileSize',
				message: t('contact.form.invalidFileSize', {
					size: CONSTANTS.MAX_FILE_SIZE_MB
				}),
				test: file => {
					if (!file || typeof file === 'string') {
						return true;
					}
					const fileSizeInMb = file.size / (1024 * 1024);

					return fileSizeInMb <= CONSTANTS.MAX_FILE_SIZE_MB;
				}
			})
			.test('fileFormat', t('contact.form.invalidFileFormat'), value => {
				return (
					!value ||
					(value && CONSTANTS.ACCEPTED_TYPES.includes(value.type))
				);
			})
	});

	const methods = useForm({
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
		defaultValues: {
			email: '',
			reason: '',
			subject: '',
			message: ''
		}
	});

	const {
		cfChallengeToken,
		cfChallengeCompleted,
		cfIdempotencyKey,
		triggerChallenge
	} = useCfTurnstileChallenge({
		action: 'contact'
	});

	async function onSubmit(values) {
		const formValues = { ...values, cfChallengeToken, cfIdempotencyKey };
		setErrorMessage('');
		setLoading(true);

		try {
			await contactModel.postForm(formValues);
			onSuccess();
		} catch (error) {
			setErrorMessage(t('errors.generic'));
		}
		setLoading(false);
	}

	const email = methods.watch('email');

	useEffect(() => {
		if (email.includes('@')) {
			triggerChallenge();
		}
	}, [email]);

	return (
		<div className="m-0 flex w-full flex-col items-center">
			<FormProvider {...methods}>
				<form
					onSubmit={methods.handleSubmit(onSubmit)}
					className="mb-7 flex w-full flex-col items-center justify-center gap-10 rounded-lg bg-white lg:gap-7"
				>
					<div className="flex w-full flex-col gap-x-5 gap-y-4 lg:gap-y-6">
						<Input
							label={`${t('contact.form.email')} *`}
							name="email"
							placeholder={`${t('contact.form.placeholder.email')}`}
							size="lg"
						/>
						<Select
							name="reason"
							required
							label={`${t('contact.form.reason')}`}
						>
							<SelectTrigger
								size="lg"
								className="rounded"
								aria-label={t('contact.form.reason')}
							>
								<SelectValue
									placeholder={`${t('contact.form.placeholder.reason')}`}
								/>
							</SelectTrigger>
							<SelectContent className="border-0 border-grey-200 bg-white">
								{reasonOptions.map((res, index) => {
									return (
										<SelectItem key={index} value={res.value}>
											{res.label}
										</SelectItem>
									);
								})}
							</SelectContent>
						</Select>

						<Input
							label={`${t('contact.form.subject')} *`}
							name="subject"
							placeholder={`${t('contact.form.placeholder.subject')}`}
							size="lg"
						/>

						<div className="lg:col-span-2 lg:grid lg:grid-cols-1">
							<FormTextArea
								className="min-h-[100px]"
								name="message"
								size="lg"
								label={`${t('contact.form.message')} *`}
								placeholder={`${t('contact.form.placeholder.message')}`}
							/>
						</div>

						<div className="lg:col-span-2 lg:grid lg:grid-cols-1">
							<ImageDropdown
								setFormFile={file => methods.setValue('file', file)}
							/>
						</div>

						<div className="lg:col-span-2 lg:grid lg:grid-cols-1">
							<CfCaptchaWidget />
						</div>
					</div>

					{errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

					<Button
						type="submit"
						loading={loading}
						disabled={!cfChallengeCompleted}
						aria-label={`${t('contact.form.button')} ${t('labels.form')}`}
					>
						{t('contact.form.button')}
					</Button>
				</form>
			</FormProvider>

			<p className="text-center text-sm leading-[23px] text-grey-500 lg:w-[456px]">
				{t('contact.form.note')}
				<a
					className="ml-1 text-sm font-bold text-red"
					href={localizedRoute(routes.privacyPolicy)}
					target="_blank"
					rel="noopener noreferrer"
				>
					{t('navigation.privacy')}
				</a>
				.
			</p>
		</div>
	);
}

export default Form;
